import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Pencil, Trash2, KeyRound, ShieldCheck, Mail, User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";

export default function UsersPage() {
  const { user: me } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (me && me.role !== "admin") navigate("/", { replace: true });
  }, [me, navigate]);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pwdTarget, setPwdTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(`Utente ${!u.active ? "attivato" : "disattivato"}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare definitivamente ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utente eliminato");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="users-page">
      <Navbar showBack />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-brand-navy">Gestione operatori</h1>
            <p className="text-sm text-slate-500 mt-1">Crea, modifica e disattiva gli utenti dell'applicazione.</p>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="brand-navy text-white" data-testid="new-user-button">
            <Plus className="w-4 h-4 mr-2" /> Nuovo utente
          </Button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 divide-y" data-testid="users-list">
          {loading && <div className="p-8 text-center text-slate-500 text-sm">Caricamento...</div>}
          {!loading && users.length === 0 && (
            <div className="p-8 text-center text-slate-500 text-sm">Nessun utente.</div>
          )}
          {users.map((u) => (
            <div key={u.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4" data-testid={`user-row-${u.id}`}>
              <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
                <UserIcon className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-800">{u.name}</span>
                  <span className={
                    "text-[10px] tracking-wider font-bold px-2 py-0.5 rounded-full " +
                    (u.role === "admin" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700")
                  }>
                    {u.role === "admin" ? "ADMIN" : "OPERATORE"}
                  </span>
                  {!u.active && (
                    <span className="text-[10px] tracking-wider font-bold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">
                      DISATTIVATO
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1">
                  <Mail className="w-3 h-3" /> {u.email}
                </div>
              </div>
              <div className="flex items-center gap-3 sm:gap-4 sm:ml-auto">
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <Switch
                    checked={u.active}
                    onCheckedChange={() => toggleActive(u)}
                    disabled={u.id === me?.id}
                    data-testid={`user-toggle-active-${u.id}`}
                  />
                  Attivo
                </label>
                <button
                  onClick={() => setPwdTarget(u)}
                  className="p-2 rounded-md hover:bg-slate-100 text-slate-500"
                  title="Cambia password"
                  data-testid={`user-password-${u.id}`}
                >
                  <KeyRound className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setEditing(u)}
                  className="p-2 rounded-md hover:bg-slate-100 text-slate-500"
                  title="Modifica"
                  data-testid={`user-edit-${u.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => remove(u)}
                  disabled={u.id === me?.id}
                  className="p-2 rounded-md hover:bg-rose-50 text-rose-500 disabled:opacity-30"
                  title="Elimina"
                  data-testid={`user-delete-${u.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <UserFormDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onSaved={() => { load(); }}
        />
        <UserFormDialog
          open={!!editing}
          onOpenChange={(v) => { if (!v) setEditing(null); }}
          initial={editing}
          onSaved={() => { load(); setEditing(null); }}
        />
        <PasswordDialog
          open={!!pwdTarget}
          onOpenChange={(v) => { if (!v) setPwdTarget(null); }}
          target={pwdTarget}
          onSaved={() => setPwdTarget(null)}
        />
      </main>
    </div>
  );
}

function UserFormDialog({ open, onOpenChange, initial = null, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({ email: "", name: "", role: "operatore", password: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({ email: initial.email, name: initial.name, role: initial.role, password: "" });
    } else {
      setForm({ email: "", name: "", role: "operatore", password: "" });
    }
    setError("");
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/users/${initial.id}`, { name: form.name, role: form.role });
      } else {
        await api.post("/users", form);
      }
      toast.success(isEdit ? "Utente aggiornato" : "Utente creato");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-brand-navy">
            {isEdit ? "Modifica utente" : "Nuovo utente"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="u-name">Nome</Label>
            <Input id="u-name" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="user-name-input" />
          </div>
          <div>
            <Label htmlFor="u-email">Email</Label>
            <Input id="u-email" type="email" required value={form.email}
              disabled={isEdit}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="user-email-input" />
          </div>
          <div>
            <Label>Ruolo</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="operatore">Operatore</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!isEdit && (
            <div>
              <Label htmlFor="u-pwd">Password</Label>
              <Input id="u-pwd" type="password" required minLength={6} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="user-password-input" />
            </div>
          )}
          {error && <div className="text-sm text-red-600" data-testid="user-form-error">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={saving} className="brand-navy text-white"
              data-testid="user-form-submit">
              {saving ? "Salvataggio..." : isEdit ? "Salva" : "Crea utente"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PasswordDialog({ open, onOpenChange, target, onSaved }) {
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setPwd(""); setError(""); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.patch(`/users/${target.id}`, { password: pwd });
      toast.success("Password aggiornata");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="user-password-dialog">
        <DialogHeader>
          <DialogTitle className="text-brand-navy">
            <ShieldCheck className="w-5 h-5 inline mr-2" />
            Cambia password{target ? ` · ${target.name}` : ""}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="np">Nuova password</Label>
            <Input id="np" type="password" required minLength={6} value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              data-testid="user-new-password-input" />
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={saving} className="brand-navy text-white"
              data-testid="user-password-submit">
              {saving ? "Salvataggio..." : "Aggiorna"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
